import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { HeaderNavLink } from '../header/header';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';

// EPS-4414: named type for the (blur) target shape, replacing the untyped `event` param
// the ins/mtg donor used — keeps this hsp copy lint-clean without widening scope.
interface BlurEventLike {
  target: {
    classList: { contains: (className: string) => boolean };
    parentNode: { id: string };
  };
}

@Component({
  selector: 'ace-header-navlink',
  templateUrl: './header-nav-link.component.html',
  standalone: true,
  imports: [CommonModule, QueryParamDirective],
})
export class HeaderNavLinkComponent {
  @Input() data: HeaderNavLink;
  showComplexMenu = false;
  showSimpleMenu = false;
  tabShift: boolean;
  hovered = false;
  faCaretDown = faCaretDown;
  faCaretUp = faCaretUp;

  constructor(private accessibilityService: AccessibilityService) {}

  get menuSize(): string | undefined {
    return this.data?.menuSize === '340px' ? 'w-[340px]' : 'w-[180px]';
  }

  get isMyAccountLink(): boolean {
    return this.data?.linkLabel === 'My Account';
  }

  /**
   * Show dropdown menus when nav item is focused and enter is pressed
   */
  enterPress(): void {
    this.showSimpleMenu = !!this.data.ddList;
    this.showComplexMenu = !!this.data.complexDdList;
  }

  /**
    * Hide dropdown menus after the last item is unfocused/blurred and tabbed forward.
    * If the last item is tabbed backwards, the menu stays open.
    * If the first item is tabbed backwards, the menu closes.
    @param event The blur event that triggered the function.
    */
  onBlur(event: BlurEventLike): void {
    const isFirstSimpleItem = event.target.classList.contains('first-simple-item');
    const isLastSimpleItem = event.target.classList.contains('last-simple-item');
    const isFirstColumnFirstItem =
      event.target.parentNode.id === 'first-column' && event.target.classList.contains('first-item');
    const isLastColumnLastItem =
      event.target.parentNode.id === 'last-column' && event.target.classList.contains('last-item');

    // Update the visibility of the menus based on the tabbing direction and item position.
    if ((isLastColumnLastItem && !this.tabShift) || (isFirstColumnFirstItem && this.tabShift)) {
      this.showComplexMenu = false;
    }
    if ((isLastSimpleItem && !this.tabShift) || (isFirstSimpleItem && this.tabShift)) {
      this.showSimpleMenu = false;
    }
  }

  /**
   * Checks if user is tabbing backwards
   * @param event The keydown KeyboardEvent that triggers the function
   */
  onKeyDown(event: KeyboardEvent): void {
    this.tabShift = event.shiftKey && event.key === 'Tab' ? true : false;
  }

  splitLabel(label: string): { firstWord: string; rest: string } {
    const index = label.indexOf(' ');
    if (index === -1) {
      return { firstWord: label, rest: '' };
    }
    return {
      firstWord: label.substring(0, index),
      rest: label.substring(index + 1),
    };
  }
}
